import React from 'react'
import styled from 'styled-components'
import { BottomStickyBar } from 'components/common/bottom-sticky-bar'
import { MediumText } from 'components/common/text'
import { Button, BtnSize, BtnType } from 'components/common/button'
import { ResponsiveLayout, Row } from 'components/common/layout'
import { theme } from 'infra/theme'
import { observer } from 'mobx-react'
import { ScoutProfileFormStore } from 'stores/scouts/profile-form'
import { useScoutRequestMyInfoFillStatus } from 'apis/hooks'
import { useState, useEffect } from 'react'
import { ScoutRequestMyInfoFillStatus } from 'types/common'
import { getRequest } from 'apis/fetcher'

export type InfoFillStatusType =
  | 'NOT_USED' // 한번도 사용하지 않은 상태
  | 'RETRY' // 한번 이상 사용한 상태
  | 'WORKING' // completedCount < 2 && status === 'NOT_AVAILABLE'
  | 'LIMITED' // completedCount === 2

// infoFillStatus를 네 가지 타입으로 분류하는 유틸
export const getScoutRequestMyInfoFillStatusType = (
  data?: ScoutRequestMyInfoFillStatus,
) => {
  // 첫 요청: completedCount 0, status NOT_AVAILABLE
  if (!data) return 'NOT_USED'
  if (data.completedCount === 0 && data.status === 'NOT_AVAILABLE') {
    return 'WORKING'
  }
  if (data.completedCount === 0 && data.status === 'AVAILABLE') {
    return 'NOT_USED'
  }
  if (data.completedCount === 2) {
    return 'LIMITED'
  }
  if (data.completedCount < 2 && data.status === 'NOT_AVAILABLE') {
    return 'WORKING'
  }
  if (data.completedCount < 2 && data.status === 'AVAILABLE') {
    return 'RETRY'
  }
  return 'NOT_USED'
}

export const MyProfileBottomBar: React.FC<{
  store: ScoutProfileFormStore
  isPublished: boolean
  completedCount: number
  onPublish: () => void
}> = observer(({ store, isPublished, onPublish }) => {
  const { data: initialInfoFillStatus } = useScoutRequestMyInfoFillStatus()
  const [infoFillStatus, setInfoFillStatus] = useState(initialInfoFillStatus)

  // polling 함수
  useEffect(() => {
    setInfoFillStatus(initialInfoFillStatus)
  }, [initialInfoFillStatus])

  useEffect(() => {
    let abort = false
    const poll = async () => {
      while (!abort && infoFillStatus?.status === 'NOT_AVAILABLE') {
        try {
          const { data } = await getRequest(
            '/scouts/requests/me/info-fill-status',
          )
          if (data) {
            setInfoFillStatus(data)
            if (data.status !== 'NOT_AVAILABLE') break
          }
        } catch (e) {
          alert(e)
        }
        await new Promise((r) => setTimeout(r, 10000))
      }
    }
    if (infoFillStatus?.status === 'NOT_AVAILABLE') {
      poll()
    }
    return () => {
      abort = true
    }
  }, [infoFillStatus?.status])

  const type = getScoutRequestMyInfoFillStatusType(infoFillStatus)

  return (
    <BottomStickyBar style={{ borderTop: `1px solid ${theme.color.line}` }}>
      <ResponsiveLayout>
        <Container>
          <DescText>
            {isPublished
              ? '스타트업에 정보가 공개되고 있으며, 언제든 비공개하고 다시 작성할 수 있어요'
              : '스타트업에 정보가 비공개 되고 있으며, 언제든 공개로 바꿀 수 있어요'}
          </DescText>
          <Row style={{ gap: 8 }}>
            <MyProfileButton
              onClick={onPublish}
              variant={
                isPublished ? BtnType.SUPER_LOW_OUTLINE : BtnType.PRIMARY2
              }
            >
              {isPublished ? '비공개 할래요' : '공개 할래요'}
            </MyProfileButton>
            <MyProfileButton
              variant={
                type === 'NOT_USED' || type === 'RETRY'
                  ? BtnType.SECONDARY
                  : BtnType.DISABLE
              }
              onClick={() => {
                if (type === 'LIMITED' || type === 'WORKING') return
                store.setIsSteveModalOpen(true)
              }}
            >
              {type === 'NOT_USED'
                ? 'AI로 프로필 정보 가져오기'
                : type === 'RETRY'
                ? 'AI에게 프로필 작성 다시 맡기기'
                : type === 'WORKING'
                ? 'AI 프로필 작성 작업 중'
                : '⚠️ 하루에 2회까지 요청 가능합니다.'}
            </MyProfileButton>
          </Row>
        </Container>
      </ResponsiveLayout>
    </BottomStickyBar>
  )
})

const Container = styled(Row)`
  justify-content: space-between;
  padding: 8px 0;
  @media ${(p) => p.theme.deviceSize.mobile} {
    gap: 8px;
  }
`

const DescText = styled(MediumText)`
  white-space: nowrap;
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-size: ${(p) => p.theme.fontSize.small};
    font-weight: ${(p) => p.theme.fontWeight.semiBold};
    color: ${(p) => p.theme.color.gray2};
  }
  @media ${(p) => p.theme.deviceSize.desktop} {
    color: ${(p) => p.theme.color.primary5};
  }
`

const MyProfileButton = styled(Button).attrs({ size: BtnSize.MEDIUM })`
  width: fit-content;
  white-space: nowrap;
  @media ${(p) => p.theme.deviceSize.desktop} {
    padding: 12px 16px;
  }
  @media ${(p) => p.theme.deviceSize.mobile} {
    padding: 8px;
  }
`
